<template>
    <div>
        <data-table
            v-if="!fetchingOrganization"
            :title="unassigned ? 'Unassigned Devices': 'Devices'"
            delete-button-text="selected devices"
            :headers="headers"
            :get-items-handler="getDevices"
            :get-delete-handler="deleteDevice"
            :get-prepared-data-to-save="prepareDataToSave"
            :reset-filters-handler="resetFilters"
            :deletable="hasAllRoles(['DeviceDelete'])"

            has-filters
        >
            <template v-slot:tableFilters>
                <devices-filters
                    :filters="filters"
                    :is-fleet="!!fleet"
                ></devices-filters>
            </template>
            <template v-slot:items="item">
                <td>
                    <template v-if="hasAllRoles(['DeviceUpdate'])">
                        <router-link :to="{ name: 'editDevice', params: { deviceUuid: item.props.item.id } }">
                            {{ item.props.item.hardwareId }}
                        </router-link>
                    </template>
                    <template v-else>
                        {{ item.props.item.hardwareId }}
                    </template>
                </td>
                <td>
                    {{ item.props.item.deviceModel ? item.props.item.deviceModel.name : '-' }}
                </td>
                <td>
                    {{ item.props.item.organization ? item.props.item.organization.name : '-' }}
                </td>
                <td>
                        <template v-if="hasAllRoles(['VehicleUpdate'])">
                            <router-link v-if="item.props.item.vehicle" :to="{ name: 'editVehicle', params: { fleetUuid: item.props.item.fleet ? item.props.item.fleet.id : '', vehicleUuid: item.props.item.vehicle ? item.props.item.vehicle.id : '' } }">
                                {{ item.props.item.vehicle.name}}
                            </router-link>
                        </template>
                        <template v-else>
                            <template v-if="item.props.item.vehicle">
                                {{ item.props.item.hardwareId }}
                            </template>
                        </template>
                </td>
                <td>
                    <template v-if="hasAllRoles(['FleetUpdate'])">
                        <router-link v-if="item.props.item.fleet" :to="{ name: 'editFleetDetails', params: { fleetUuid: item.props.item.fleet.id } }">
                            {{ item.props.item.fleet.name}}
                        </router-link>
                    </template>
                    <template v-else>
                        <template v-if="item.props.item.vehicle">
                            {{ item.props.item.fleet.name}}
                        </template>
                    </template>
                </td>

                <td>
                    <template v-if="item.props.item.lastReported">
                        {{ item.props.item.lastReported | moment }}
                    </template>
                </td>

                <td>{{ item.props.item.enabled ? 'YES': 'NO' }}</td>
                <td v-if="hasAllRoles(['DeviceUpdate'])" class="text-center">
                    <router-link :to="{ name: 'editDevice', params: { deviceUuid: item.props.item.id } }">
                        <sp-button design-type="table-button" icon="edit"></sp-button>
                    </router-link>
                </td>
            </template>

            <template v-slot:createButton>
                <router-link
                    v-show="hasAllRoles(['DeviceAdd'])"
                    :to="'/settings/devices/create' +
                        (vehicleId === null? '': `?vehicleId=${vehicleId}`) +
                        (fleet == null? '' : `?fleetId=${fleet.id}`)
                    "
                    class="no-underline">
                    <sp-button sp-class="no-padding-right" icon="settings" text="Add device"></sp-button>
                </router-link>
            </template>

            <template v-slot:createMultipleButton>
                <sp-button
                    v-show="hasAllRoles(['DeviceAdd'])"
                    @click.native="showDevicesBulkUpload = !showDevicesBulkUpload"
                    sp-class="no-padding-right"
                    icon="settings"
                    text="Add multiple devices"
                ></sp-button>
            </template>
        </data-table>
        <sp-loader v-if="!isDeviceTableReady"></sp-loader>
        <devices-bulk-upload-dialog v-model="showDevicesBulkUpload"></devices-bulk-upload-dialog>
    </div>
</template>

<script>

import { mapGetters, mapActions } from 'vuex'
import DataTable from '@/views/_components/DataTable'
import SpButton from '@/components/forms/Button'
import toast from '@/plugins/izitoast'
import DevicesBulkUploadDialog from '@/views/Devices/components/DevicesBulkUploadDialog'
import DevicesFilters from '@/views/Devices/components/DevicesFilters'

import DeviceApi from '@/api/DeviceApi'
import OrganizationApi from '@/api/OrganizationApi'
import moment from 'moment'
import SpLoader from '@/components/atoms/Loader'
import { setTableFiltersToLocalStorage, getTableFiltersFromLocalStorage } from '@/helpers/StorageHelper'

const deviceApi = new DeviceApi()
const organizationApi = new OrganizationApi()

export default {
    name: 'DevicesTable',
    components: {
        DevicesFilters,
        DataTable,
        SpButton,
        DevicesBulkUploadDialog,
        SpLoader
    },
    filters: {
        moment: function (date) {
            return moment(date).fromNow()
        }
    },
    props: {
        vehicleId: {
            type: String,
            required: false,
            default: null
        },
        unassigned: {
            type: Boolean,
            required: false,
            default: false
        },
        fleet: {
            type: Object,
            required: false,
            default: null
        }
    },
    data () {
        return {
            headers: [
                {
                    text: 'Recorder Id',
                    sortable: true,
                    value: 'hardwareId'
                },
                {
                    text: 'Device model',
                    sortable: true,
                    value: 'deviceModelId'
                },
                {
                    text: 'Organization',
                    sortable: true,
                    value: 'Organization.Name'
                },
                {
                    text: 'Vehicle',
                    sortable: true,
                    value: 'Vehicle.RegistrationNumber'
                },
                {
                    text: 'Fleet group',
                    sortable: true,
                    value: 'Fleet.Name'
                },
                {
                    text: 'Last reported',
                    sortable: false,
                    value: 'lastReported'
                },
                {
                    text: 'Enabled',
                    sortable: true,
                    value: 'Enabled'
                },
                {
                    text: 'Edit',
                    sortable: false,
                    value: 'isEditable',
                    class: !this.hasAllRoles(['DeviceUpdate']) ? 'd-none' : ''
                },
                {
                    text: 'Delete',
                    sortable: false,
                    value: 'isDeletable',
                    class: !this.hasAllRoles(['DeviceDelete']) ? 'd-none' : ''
                }
            ],
            totalResults: 0,
            showDevicesBulkUpload: false,
            fetchingOrganization: false,
            fetchingDevices: false,
            filters: {
                name: null,
                organization: null,
                fleet: null,
                deviceModelId: null,
                deviceProtocolId: null,
                vehicle: null,
                simCard: null,
                enabled: null,
                neverReporter: null
            }
        }
    },
    computed: {
        ...mapGetters('authModule', [
            'getActiveOrganization',
            'getIsImpersonateActive'
        ]),
        ...mapGetters('managementTables', [
            'getDeviceFilters'
        ]),
        isDeviceTableReady () {
            return !this.fetchingOrganization && !this.fetchingDevices
        }
    },
    async mounted () {
        this.applySavedFilters();
        if (this.fleet) {
            this.filters.fleet = this.fleet
        }
    },
    methods: {
        ...mapActions('managementTables', [
            'setDeviceFilters'
        ]),
        async getDevices (page, rowsPerPage, recorderId, sortBy) {
            this.fetchingDevices = true
            const response = await deviceApi.getDevices({
                page: page,
                perPage: rowsPerPage,
                vehicleId: this.filters.vehicle ? this.filters.vehicle.id : this.vehicleId,
                name: this.filters.name,
                fleets: this.filters.fleet ? this.filters.fleet : null,
                deviceModelId: this.filters.deviceModelId,
                deviceProtocolId: this.filters.deviceProtocolId,
                organizations: this.filters.organization ? this.filters.organization : null,
                simCard: this.filters.simCard,
                unassignedToVehicle: this.unassigned ? this.unassigned : null,
                enabled: this.filters.enabled,
                neverReporter: this.filters.neverReporter,
                orderBy: sortBy
            })
            this.totalResults = response.payload.totalResults
            this.fetchingDevices = false

            if (!this.getIsImpersonateActive) {
                this.setDeviceFilters(this.filters)
                setTableFiltersToLocalStorage('devices', this.filters)
            }

            return {
                total: response.payload.totalResults,
                items: response.payload.items
            }
        },
        async applySavedFilters () {
            if (!this.getIsImpersonateActive) {
                const savedFilters = getTableFiltersFromLocalStorage('devices')
                if (savedFilters) {
                    this.filters = { ...savedFilters }
                    return
                }
            } else {
                this.filters.name = null
                this.filters.organization = null
                this.filters.fleet = null
                this.filters.deviceModelId = null
                this.filters.deviceProtocolId = null
                this.filters.vehicle = null
                this.filters.simCard = null
                this.filters.enabled = null
                this.filters.neverReporter = null
            }
            await this.getOrganization()
        },
        async getOrganization () {
            if (this.filters.organization) return
            this.fetchingOrganization = true
            const response = await organizationApi.getOrganizationById(this.getActiveOrganization)
            if (!response.isSuccess) {
                this.fetchingOrganization = false
                return
            }
            this.filters.organization = response.payload
            this.fetchingOrganization = false
        },
        async deleteDevice (device) {
            const response = await deviceApi.deleteDevice(device.id)

            if (!response.isSuccess) {
                return
            }

            toast.success(`Device ${device.hardwareId} successfully deleted.`)
        },
        async prepareDataToSave (search) {
            const dataToSave = []
            const response = await deviceApi.getDevices({
                vehicleId: this.filters.vehicle ? this.filters.vehicle.id : this.vehicleId,
                name: this.filters.name,
                fleets: this.filters.fleet ? this.filters.fleet : null,
                deviceModelId: this.filters.deviceModelId,
                deviceProtocolId: this.filters.deviceProtocolId,
                organization: this.filters.organization ? this.filters.organization : null,
                simCard: this.filters.simCard,
                enabled: this.filters.enabled,
                neverReporter: this.filters.neverReporter
            })

            if (!response.isSuccess) {
                return
            }

            response.payload.items.forEach(item => {
                dataToSave.push({
                    recorderId: item.hardwareId ? item.hardwareId : ' ',
                    vehicle: item.vehicle ? item.vehicle.name : ' ',
                    fleetGroup: item.fleet ? item.fleet.name : ' ',
                    disabled: item.enabled ? 'No' : 'Yes',
                    lastReported: item.lastReported ? moment(item.lastReported).fromNow().toString() : ' '
                })
            })
            return dataToSave
        },
        resetFilters () {
            this.filters = {
                name: null,
                organization: this.getIsImpersonateActive ? this.filters.organization : null,
                fleet: this.fleet ? this.fleet : null,
                deviceModelId: null,
                deviceProtocolId: null,
                vehicle: null,
                simCard: null,
                enabled: null,
                neverReporter: null
            }
        },
        moment: function () {
            return moment()
        }
    }
}
</script>

<style scoped>
.margin-left {
    margin-left: 10px !important;
}
</style>
